// src/components/ScrollToTop.js
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

const ScrollToTop = () => {
  const { pathname } = useLocation();
  const scrollLockRef = useRef(false);
  const isNavigatingRef = useRef(false);

  useEffect(() => {
    // Disable browser's automatic scroll restoration
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  useEffect(() => {
    // Mark that we're in a navigation state
    isNavigatingRef.current = true;
    
    // Short lock period only for navigation - 1 second should be enough
    scrollLockRef.current = true;
    
    // More aggressive scroll to top function
    const forceScrollToTop = () => {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      
      // Also clear any stored scroll positions that might be restored later
      sessionStorage.removeItem('productListScrollPosition');
      sessionStorage.removeItem('homeScrollPosition');
    };

    // Override window.scrollTo only during navigation lock period
    const originalScrollTo = window.scrollTo;
    window.scrollTo = function(...args) {
      if (scrollLockRef.current && isNavigatingRef.current) {
        originalScrollTo.call(window, { top: 0, left: 0, behavior: 'auto' });
      } else {
        originalScrollTo.apply(window, args);
      }
    };

    // Immediate scroll
    forceScrollToTop();

    // Multiple follow-up scrolls only during the initial navigation period
    const timers = [
      setTimeout(() => { 
        if (isNavigatingRef.current) {
          forceScrollToTop(); 
        }
      }, 10),
      setTimeout(() => { 
        if (isNavigatingRef.current) {
          forceScrollToTop(); 
        }
      }, 50),
      setTimeout(() => { 
        if (isNavigatingRef.current) {
          forceScrollToTop(); 
        }
      }, 100),
      setTimeout(() => { 
        if (isNavigatingRef.current) {
          forceScrollToTop(); 
        }
      }, 200),
      setTimeout(() => { 
        if (isNavigatingRef.current) {
          forceScrollToTop(); 
        }
      }, 500),
      setTimeout(() => { 
        if (isNavigatingRef.current) {
          forceScrollToTop(); 
        }
      }, 800)
    ];

    // Release the scroll lock after 1 second and allow normal scrolling
    const releaseLock = setTimeout(() => {
      scrollLockRef.current = false;
      isNavigatingRef.current = false;
      window.scrollTo = originalScrollTo;
    }, 1000);

    // Handle browser back/forward button navigation
    const handlePopState = () => {
      isNavigatingRef.current = true;
      scrollLockRef.current = true;
      forceScrollToTop();
      
      setTimeout(() => {
        scrollLockRef.current = false;
        isNavigatingRef.current = false;
        window.scrollTo = originalScrollTo;
      }, 1000);
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      scrollLockRef.current = false;
      isNavigatingRef.current = false;
      window.scrollTo = originalScrollTo;
      timers.forEach(timer => clearTimeout(timer));
      clearTimeout(releaseLock);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [pathname]);

  return null;
};

export default ScrollToTop;
